import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, inject, signal, viewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { PhotoDescriptor, PhotosApiService } from '@core/api/services/photos-api.service';
import { CirclePhotoDraft, CirclePhotoEditorComponent } from './circle-photo-editor.component';

@Component({
  selector: 'app-community-photo-carousel',
  standalone: true,
  imports: [CommonModule, MatIconModule, CirclePhotoEditorComponent],
  template: `
    <section class="carousel" [class.carousel--expanded]="expanded" [class.carousel--compact-mobile]="compactOnMobile" role="region"
      aria-roledescription="carrusel" aria-label="Fotos de la comunidad"
      (keydown.arrowleft)="navigate(-1); $event.preventDefault()"
      (keydown.arrowright)="navigate(1); $event.preventDefault()">
      <div class="carousel-stage" [class.carousel-stage--editing]="expanded && editable">
      <div class="carousel-viewport" *ngIf="current"
        (pointerdown)="startSwipe($event)" (pointerup)="endSwipe($event)" (pointercancel)="cancelSwipe()">
        @for (photo of photos; track photo.id; let position = $index) {
          <div class="carousel-slide"
            [class.carousel-slide--current]="position === index"
            [class.carousel-slide--previous]="position === index - 1"
            [class.carousel-slide--next]="position === index + 1"
            [class.carousel-slide--before]="position < index - 1"
            [class.carousel-slide--after]="position > index + 1"
            [attr.aria-hidden]="position < index - 1 || position > index + 1 ? true : null"
            [attr.role]="expanded && position === index ? 'group' : 'button'"
            [attr.tabindex]="position < index - 1 || position > index + 1 || (expanded && position === index) ? -1 : 0"
            [attr.aria-label]="position === index ? 'Ampliar fotos de la comunidad' : position < index ? 'Ver foto anterior' : 'Ver foto siguiente'"
            (keydown.enter)="position === index ? open() : navigateFromClick(position - index); $event.preventDefault()"
            (keydown.space)="position === index ? open() : navigateFromClick(position - index); $event.preventDefault()"
            (click)="position === index ? open() : navigateFromClick(position - index)">
            <img *ngIf="!(expanded && editable)" [src]="imageUrls().get(photo.thumbnailUrl) || null" [hidden]="!imageUrls().has(photo.thumbnailUrl)"
              [alt]="'Foto ' + (position + 1) + ' de la comunidad'" draggable="false" />
            <app-circle-photo-editor *ngIf="expanded && editable" [photo]="photo" [disabled]="saving || position !== index" [active]="position === index"
              [thumbnailUrl]="imageUrls().get(photo.thumbnailUrl)" (framingChange)="framingSave.emit($event)" />
            <span *ngIf="position === index && !editable && loadingPaths().has(photo.thumbnailUrl)" class="carousel-loading" role="status" aria-label="Cargando foto"></span>
            <span *ngIf="!expanded && position === index" class="carousel-expand"><mat-icon aria-hidden="true">open_in_full</mat-icon></span>
          </div>
        }
        <div *ngIf="expanded && editable" class="carousel-photo-options" (pointerdown)="$event.stopPropagation()">
          <ng-content select="[carouselPhotoOptions]" />
        </div>
        <ng-container *ngIf="previous">
          <button type="button" class="carousel-arrow carousel-arrow--previous" aria-label="Foto anterior" [disabled]="saving || editor()?.dirty()" (click)="navigateFromClick(-1)">
            <mat-icon aria-hidden="true">chevron_left</mat-icon>
          </button>
        </ng-container>
        <ng-container *ngIf="next">
          <button type="button" class="carousel-arrow carousel-arrow--next" aria-label="Foto siguiente" [disabled]="saving || editor()?.dirty()" (click)="navigateFromClick(1)">
            <mat-icon aria-hidden="true">chevron_right</mat-icon>
          </button>
        </ng-container>
      </div>
      @if (editor(); as framing) {
        <label class="carousel-zoom" title="Zoom del encuadre" (keydown)="$event.stopPropagation()">
          <mat-icon aria-hidden="true">add</mat-icon>
          <input type="range" min="1" [max]="framing.maxZoom" step="0.01" [value]="framing.zoom"
            [disabled]="saving || !framing.ready()" (input)="framing.setZoom(+$any($event.target).value)" (change)="framing.commit()"
            aria-label="Zoom del encuadre" aria-orientation="vertical" />
          <mat-icon aria-hidden="true">remove</mat-icon>
        </label>
      }
      </div>
      <span *ngIf="photos.length > 1" class="carousel-counter" role="status" aria-live="polite" aria-atomic="true">
        <span class="visually-hidden">Foto </span>{{ index + 1 }} / {{ photos.length }}
      </span>
      @if (editor(); as framing) {
        <div class="carousel-framing">
          <p>Arrastrá la imagen del círculo para acomodarla.</p>
        </div>
      }
    </section>
  `,
  styleUrl: './community-photo-carousel.component.scss',
})
export class CommunityPhotoCarouselComponent implements OnChanges, OnDestroy {
  @Input() photos: PhotoDescriptor[] = [];
  private readonly selectedId = signal<string | undefined>(undefined);
  @Input() get activeId() { return this.selectedId(); }
  set activeId(value: string | undefined) { this.selectedId.set(value); }
  @Input() expanded = false;
  @Input() editable = false;
  @Input() saving = false;
  @Input() compactOnMobile = false;
  @Output() activeIdChange = new EventEmitter<string>();
  @Output() enlarge = new EventEmitter<void>();
  @Output() framingSave = new EventEmitter<CirclePhotoDraft>();
  private readonly editors = viewChildren(CirclePhotoEditorComponent);
  editor() { return this.editors().find(editor => editor.photo.id === this.current?.id); }
  private readonly photoApi = inject(PhotosApiService);
  readonly imageUrls = signal(new Map<string, string>());
  readonly loadingPaths = signal(new Set<string>());
  private readonly retained = new Map<string, object>();
  private swipeStart?: { x: number; y: number; pointerId: number };
  private suppressOpenUntil = 0;

  ngOnChanges() {
    const paths = new Set(this.photos.map(photo => photo.thumbnailUrl));
    for (const path of this.retained.keys()) {
      if (!paths.has(path)) { this.retained.delete(path); this.photoApi.release(path); }
    }
    this.imageUrls.update(urls => new Map([...urls].filter(([path]) => paths.has(path))));
    this.loadingPaths.update(loading => new Set([...loading].filter(path => paths.has(path))));
    for (const path of paths) {
      if (this.retained.has(path)) continue;
      const request = {};
      this.retained.set(path, request);
      this.loadingPaths.update(loading => new Set(loading).add(path));
      void this.photoApi.image(path).then(async url => {
        const image = new Image();
        image.src = url;
        await image.decode();
        if (this.retained.get(path) === request) this.imageUrls.update(urls => new Map(urls).set(path, url));
      }).finally(() => {
        if (this.retained.get(path) === request) {
          this.loadingPaths.update(loading => { const next = new Set(loading); next.delete(path); return next; });
        }
      }).catch(() => {
        if (this.retained.get(path) === request) { this.retained.delete(path); this.photoApi.release(path); }
      });
    }
  }

  get index() {
    const selected = this.photos.findIndex(photo => photo.id === this.activeId);
    return selected >= 0 ? selected : Math.max(0, this.photos.findIndex(photo => photo.isCover));
  }
  get current() { return this.photos[this.index]; }
  get previous() { return this.neighbor(-1); }
  get next() { return this.neighbor(1); }
  private neighbor(offset: number) {
    const targetIndex = this.index + offset;
    return targetIndex >= 0 && targetIndex < this.photos.length ? this.photos[targetIndex] : undefined;
  }
  navigate(offset: number) {
    if (this.saving || this.editor()?.dirty()) return;
    const photo = this.neighbor(offset);
    if (!photo) return;
    this.activeId = photo.id;
    this.activeIdChange.emit(photo.id);
  }
  open() { if (!this.expanded && Date.now() >= this.suppressOpenUntil) this.enlarge.emit(); }
  navigateFromClick(offset: number) { if (Date.now() >= this.suppressOpenUntil) this.navigate(offset); }
  startSwipe(event: PointerEvent) {
    if (event.pointerType === 'touch') this.swipeStart = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
  }
  endSwipe(event: PointerEvent) {
    const start = this.swipeStart;
    this.cancelSwipe();
    if (!start || start.pointerId !== event.pointerId) return;
    const dx = event.clientX - start.x, dy = event.clientY - start.y;
    if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy)) {
      this.navigate(dx < 0 ? 1 : -1);
      this.suppressOpenUntil = Date.now() + 500;
      event.preventDefault();
    }
  }
  cancelSwipe() { this.swipeStart = undefined; }
  ngOnDestroy() { for (const path of this.retained.keys()) this.photoApi.release(path); this.retained.clear(); }
}
